// Shared `args` shapes for registry entries.
//
// Most entries fall into one of a handful of structural buckets — no
// args, one numeric, one pattern, two numerics, etc. Encoding each
// bucket as a shared constant or factory keeps each entry's `args`
// declaration to one line and lets us tighten validation in one place
// if a bucket's contract changes.
//
// Per the brand voice rules in `types.ts`, the per-arg `doc` field is
// either empty (when the entry's top-level `doc` already describes
// the single arg, the common case) or a short specific phrase — never
// generic boilerplate like "the value to apply."

import type { ArgSchema } from './types';

export const NO_ARGS: readonly ArgSchema[] = [];

// One numeric. The arg's prose is carried by the entry's `doc`; the
// `name` is `'value'` so the schema is interchangeable across the ~70
// effects that fit this shape.
export const SINGLE_NUMERIC: readonly ArgSchema[] = [
  { name: 'value', type: 'numeric', required: true, doc: '' },
];

// One mini-notation pattern (`struct("t ~ t t")`, `vowel("a e o")`).
export const SINGLE_PATTERN: readonly ArgSchema[] = [
  { name: 'pattern', type: 'pattern', required: true, doc: '' },
];

// One literal string (`s("sawtooth")`, `chord("Cmaj7")`). Distinct
// from SINGLE_PATTERN: strings are not parsed as mini-notation.
export const SINGLE_STRING: readonly ArgSchema[] = [
  { name: 'value', type: 'string', required: true, doc: '' },
];

// Two numerics — used by zoom, compress, bjork, etc. Caller supplies
// arg names since they're meaningful per-kind (start/end, pulses/
// steps, …).
export function twoNumeric(
  a: { name: string; doc?: string },
  b: { name: string; doc?: string },
): readonly ArgSchema[] {
  return [
    { name: a.name, type: 'numeric', required: true, doc: a.doc ?? '' },
    { name: b.name, type: 'numeric', required: true, doc: b.doc ?? '' },
  ];
}

// Three numerics — used by echo (n, shift, feedback).
export function threeNumeric(
  a: { name: string; doc?: string },
  b: { name: string; doc?: string },
  c: { name: string; doc?: string },
): readonly ArgSchema[] {
  return [
    { name: a.name, type: 'numeric', required: true, doc: a.doc ?? '' },
    { name: b.name, type: 'numeric', required: true, doc: b.doc ?? '' },
    { name: c.name, type: 'numeric', required: true, doc: c.doc ?? '' },
  ];
}
